import { motion } from "framer-motion";
import { Briefcase, Cog, Server, Truck } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 22 — What this means for each of you.
 * Four role cards + the framing & closing quotes.
 */

const roles = [
  {
    icon: Briefcase,
    role: "RDs / AMs",
    impact: "Stronger client offering, easier upsell",
    color: "#96C11F",
  },
  {
    icon: Cog,
    role: "Operations",
    impact: "Reduced manual workload",
    color: "#00deb6",
  },
  {
    icon: Server,
    role: "IT",
    impact: "Structured, scalable systems (not scattered tools)",
    color: "#e9a820",
  },
  {
    icon: Truck,
    role: "Delivery",
    impact: "Faster turnaround without sacrificing quality",
    color: "#e50064",
  },
];

export default function Scene22_WhatThisMeans() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-what-this-means">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 25% 30%, rgba(150,193,31,0.16) 0%, transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(0,222,182,0.10) 0%, transparent 55%)",
            "radial-gradient(ellipse at 75% 30%, rgba(0,222,182,0.14) 0%, transparent 55%), radial-gradient(ellipse at 25% 70%, rgba(229,0,100,0.10) 0%, transparent 55%)",
            "radial-gradient(ellipse at 25% 30%, rgba(150,193,31,0.16) 0%, transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(0,222,182,0.10) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-5"
        >
          What this means · for each of you
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-5xl lg:text-6xl text-white"
          data-testid="wtm-headline"
        >
          Impact across{" "}
          <span style={{ color: "#96C11F" }}>the team.</span>
        </motion.h2>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roles.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.role}
                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 0.6 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -4 }}
                className="relative rounded-2xl p-7 glass overflow-hidden"
                data-testid={`wtm-role-${i}`}
              >
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  animate={{ opacity: [0.4, 0.85, 0.4] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.4,
                  }}
                  style={{
                    background: `radial-gradient(ellipse at 50% 0%, ${r.color}28 0%, transparent 60%)`,
                  }}
                />
                <div className="relative">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: `${r.color}1a`,
                      color: r.color,
                      border: `1px solid ${r.color}55`,
                    }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div
                    className="text-[11px] tracking-[0.32em] uppercase mb-2"
                    style={{ color: r.color }}
                  >
                    {r.role}
                  </div>
                  <div className="text-base sm:text-lg text-white leading-snug">
                    {r.impact}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </SceneShell>
  );
}
